/**
 * "Inspect" for a pe3d model slot: the slot's .glb — the game's own, a
 * shipped one or a library file — in a large three.js view
 * (modules/pe3dModelViewer.js, fetched only when this opens). Rotate, zoom
 * and pan, play its animation clips, look at the wireframe; the numbers the
 * slot is checked against (triangles, size) sit underneath.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";
import { CONTROL_BORDER, FIELD_LABEL } from "../snapshotEditor/panelPalette";
import { DIALOG_PAPER_PROPS, DIALOG_TEXT, MUTED } from "./pe3dPanelKit";

const NO_CLIP = "";

const fileName = (path) => String(path || "").split("/").pop();
const formatBytes = (bytes) => (bytes >= 1e6 ? `${(bytes / 1e6).toFixed(2)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/**
 * @param props.open
 * @param props.onClose
 * @param props.title     the slot's label
 * @param props.load      () => Promise<{ url, file } | null> — the slot's .glb
 * @param props.limits    the slot's limits line, if any
 * @param props.bytes     the file's size, if known
 */
export default function DialogPe3dModelInspect({ open, onClose, title, load, limits, bytes }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  const [file, setFile] = useState("");
  const [clips, setClips] = useState([]);
  const [stats, setStats] = useState(null);
  const [clip, setClip] = useState(NO_CLIP);
  const [isPaused, setIsPaused] = useState(false);
  const [isWireframe, setIsWireframe] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState("loading");
    setError("");
    setClips([]);
    setStats(null);
    setClip(NO_CLIP);
    setIsPaused(false);
    setIsWireframe(false);
    (async () => {
      try {
        const source = await load();
        if (!source) throw new Error("This slot's file could not be read.");
        if (cancelled) return;
        setFile(source.file);
        const { createModelViewer } = await import(/* webpackChunkName: "pe3dModelViewer" */ "../../modules/pe3dModelViewer");
        // The dialog mounts its content after the transition starts; wait for the box.
        while (!containerRef.current && !cancelled) await new Promise((resolve) => setTimeout(resolve, 16));
        if (cancelled) return;
        const viewer = await createModelViewer(containerRef.current, source.url);
        if (cancelled) {
          viewer.dispose();
          return;
        }
        viewerRef.current = viewer;
        setClips(viewer.clips);
        setStats(viewer.stats);
        if (viewer.clips.length) {
          setClip(viewer.clips[0]);
          viewer.playClip(viewer.clips[0]);
        }
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        setError(err && err.message ? err.message : String(err));
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
      if (viewerRef.current) viewerRef.current.dispose();
      viewerRef.current = null;
    };
  }, [open]);

  const viewer = viewerRef.current;
  const details = [
    stats && `${stats.triangles.toLocaleString()} triangles`,
    stats && `${stats.meshes} mesh${stats.meshes === 1 ? "" : "es"}`,
    bytes && formatBytes(bytes),
    `${clips.length} clip${clips.length === 1 ? "" : "s"}`,
  ].filter(Boolean);

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="lg" fullWidth PaperProps={DIALOG_PAPER_PROPS}>
      <DialogTitle>
        {title}
        {file && <span style={{ fontSize: 13, color: MUTED, marginLeft: 8 }}>{fileName(file)}</span>}
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            position: "relative",
            height: "65vh",
            minHeight: 320,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 0 0 1px ${CONTROL_BORDER}`,
            backgroundColor: "#232326",
          }}
        >
          <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
          {state === "loading" && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <CircularProgress size={28} />
            </div>
          )}
          {state === "error" && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
              <Typography style={{ ...DIALOG_TEXT.failure, marginTop: 0 }}>Could not show the model — {error}</Typography>
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
          {clips.length > 0 && (
            <>
              <span style={{ fontSize: 12, color: FIELD_LABEL }}>Animation</span>
              <Select
                disableUnderline
                value={clip}
                style={{ fontSize: "12.5px", minWidth: 140 }}
                onChange={(event) => {
                  setClip(event.target.value);
                  setIsPaused(false);
                  if (viewer) {
                    viewer.setPaused(false);
                    viewer.playClip(event.target.value || null);
                  }
                }}
              >
                <MenuItem value={NO_CLIP} style={{ fontSize: "12.5px" }}>
                  <em>None (bind pose)</em>
                </MenuItem>
                {clips.map((name) => (
                  <MenuItem key={name} value={name} style={{ fontSize: "12.5px" }}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
              <Tooltip title={isPaused ? "Play" : "Pause"}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!clip}
                    onClick={() => {
                      setIsPaused(!isPaused);
                      if (viewer) viewer.setPaused(!isPaused);
                    }}
                  >
                    {isPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={isWireframe}
                disabled={state !== "ready"}
                onChange={(event) => {
                  setIsWireframe(event.target.checked);
                  if (viewer) viewer.setWireframe(event.target.checked);
                }}
              />
            }
            label={<span style={{ fontSize: 13 }}>Wireframe</span>}
            style={{ marginLeft: 0 }}
          />
          <Tooltip title="Frame the model again">
            <span>
              <IconButton size="small" disabled={state !== "ready"} onClick={() => viewer && viewer.resetView()}>
                <CenterFocusStrongIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: FIELD_LABEL }}>{details.join(" · ")}</span>
        </div>
        <Typography style={DIALOG_TEXT.hint}>
          Drag to rotate, scroll to zoom, right-drag to pan.{limits ? ` The slot takes ${limits}.` : ""}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
